import type { ReactNode } from 'react';
import {
  Select,
  type SelectContentProps,
  type SelectOption,
  type SelectOptions,
  type SelectPortalProps,
  type SelectProps as GVUISelectProps,
} from '@globalvisioninc/ui/select';

export interface GVSelectOption extends SelectOption {
  value: string;
  label: string;
}

export interface GVSelectProps {
  data?: ReadonlyArray<GVSelectOption>;
  options: SelectOptions;
  value?: string | null;
  defaultValue?: string | null;
  onValueChange?: (value: string) => void;
  label?: ReactNode;
  placeholder?: string;
  id?: string;
  name?: string;
  className?: string;
  isDisabled?: boolean;
  contentClassName?: string;
  contentProps?: Omit<SelectContentProps, 'className' | 'children' | 'options' | 'renderOption'>;
  container?: SelectPortalProps['container'];
  isLabelHidden?: boolean;
  isLabelDynamic?: boolean;
  renderValue?: GVUISelectProps['renderValue'];
  renderOption?: SelectContentProps['renderOption'];
  /** Panel children composed beside options (e.g. `Select.Arrow`). */
  children?: ReactNode;
}

const GVSelect = ({
  options,
  value,
  defaultValue,
  onValueChange,
  label,
  placeholder,
  id,
  name,
  className,
  isDisabled,
  contentClassName,
  contentProps,
  container,
  isLabelHidden,
  isLabelDynamic,
  renderValue,
  renderOption,
  children,
}: GVSelectProps) => {
  return (
    <Select.Root
      id={id}
      name={name}
      label={label}
      placeholder={placeholder}
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      isDisabled={isDisabled}
      className={className}
      isLabelHidden={isLabelHidden}
      isLabelDynamic={isLabelDynamic}
      renderValue={renderValue}
    >
      <Select.Trigger />
      <Select.Portal container={container}>
        <Select.Content options={options} renderOption={renderOption} className={contentClassName} {...contentProps}>
          {children}
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};

export default GVSelect;
